import Button from 'devextreme-react/button';
import CheckBox from 'devextreme-react/check-box';
import DataGrid, {
  Column,
  ColumnChooser,
  HeaderFilter,
  Item,
  Paging,
  Scrolling,
  SearchPanel,
  Toolbar,
  type DataGridRef
} from 'devextreme-react/data-grid';
import TextArea from 'devextreme-react/text-area';
import TextBox from 'devextreme-react/text-box';
import { useCallback, useEffect, useRef, useState } from 'react';
import { GridFormPopup, OPS_SITE_GRID_PROPS, OpsGridStateStoring, onGridRowDblClick, opsGridFormPopupSizeKey } from '../opsDataGrid';
import { PatientTrackingGridToolbarItems } from './PatientTrackingGridToolbar';
import { patientTrackingApi } from './patientTrackingApi';
import type { OurPharmacyDto, OurPharmacyUpsertBody } from './patientTrackingTypes';
import './patientTracking.css';

export interface OurPharmaciesPageProps {
  stateStorageKey?: string;
}

const OUR_PHARMACIES_GRID_SCHEMA_VERSION = 1;
const OUR_PHARMACIES_GRID_COLUMNS = [
  'pharmacyName',
  'legalName',
  'npi',
  'nabp',
  'mainPhone',
  'fax',
  'email',
  'website',
  'city',
  'state',
  'postalCode',
  'primaryContactName',
  'primaryContactEmail',
  'isActive',
  'modified',
  'modifiedBy'
] as const;

const emptyForm = (): OurPharmacyUpsertBody => ({
  pharmacyName: '',
  legalName: null,
  npi: null,
  nabp: null,
  addressLine1: null,
  addressLine2: null,
  city: null,
  state: null,
  postalCode: null,
  country: 'US',
  mainPhone: null,
  fax: null,
  email: null,
  website: null,
  primaryContactName: null,
  primaryContactTitle: null,
  primaryContactEmail: null,
  primaryContactPhone: null,
  notes: null,
  isActive: true
});

function toBody(row: OurPharmacyDto): OurPharmacyUpsertBody {
  return {
    pharmacyName: row.pharmacyName,
    legalName: row.legalName,
    npi: row.npi,
    nabp: row.nabp,
    addressLine1: row.addressLine1,
    addressLine2: row.addressLine2,
    city: row.city,
    state: row.state,
    postalCode: row.postalCode,
    country: row.country,
    mainPhone: row.mainPhone,
    fax: row.fax,
    email: row.email,
    website: row.website,
    primaryContactName: row.primaryContactName,
    primaryContactTitle: row.primaryContactTitle,
    primaryContactEmail: row.primaryContactEmail,
    primaryContactPhone: row.primaryContactPhone,
    notes: row.notes,
    isActive: row.isActive
  };
}

function trimBody(body: OurPharmacyUpsertBody): OurPharmacyUpsertBody {
  const opt = (value: string | null) => value?.trim() || null;
  return {
    pharmacyName: body.pharmacyName.trim(),
    legalName: opt(body.legalName),
    npi: opt(body.npi),
    nabp: opt(body.nabp),
    addressLine1: opt(body.addressLine1),
    addressLine2: opt(body.addressLine2),
    city: opt(body.city),
    state: opt(body.state),
    postalCode: opt(body.postalCode),
    country: opt(body.country),
    mainPhone: opt(body.mainPhone),
    fax: opt(body.fax),
    email: opt(body.email),
    website: opt(body.website),
    primaryContactName: opt(body.primaryContactName),
    primaryContactTitle: opt(body.primaryContactTitle),
    primaryContactEmail: opt(body.primaryContactEmail),
    primaryContactPhone: opt(body.primaryContactPhone),
    notes: opt(body.notes),
    isActive: body.isActive
  };
}

export function OurPharmaciesPage({ stateStorageKey = 'our-pharmacies-grid' }: OurPharmaciesPageProps) {
  const gridRef = useRef<DataGridRef>(null);
  const [rows, setRows] = useState<OurPharmacyDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<OurPharmacyUpsertBody>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await patientTrackingApi.listPharmacies());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load pharmacies.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const openCreate = useCallback(() => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError(null);
    setEditorOpen(true);
  }, []);

  const openEdit = useCallback((row: OurPharmacyDto) => {
    setEditingId(row.id);
    setForm(toBody(row));
    setFormError(null);
    setEditorOpen(true);
  }, []);

  const save = async () => {
    setSaving(true);
    setFormError(null);
    try {
      const body = trimBody(form);
      if (!body.pharmacyName) throw new Error('Pharmacy name is required.');
      const dto = editingId
        ? await patientTrackingApi.updatePharmacy(editingId, body)
        : await patientTrackingApi.createPharmacy(body);
      setRows((prev) => {
        const idx = prev.findIndex((r) => r.id === dto.id);
        if (idx === -1) return [dto, ...prev].sort((a, b) => a.pharmacyName.localeCompare(b.pharmacyName));
        const next = prev.slice();
        next[idx] = dto;
        return next.sort((a, b) => a.pharmacyName.localeCompare(b.pharmacyName));
      });
      setEditorOpen(false);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editingId) return;
    if (!window.confirm('Delete this pharmacy?')) return;
    setSaving(true);
    try {
      await patientTrackingApi.deletePharmacy(editingId);
      setEditorOpen(false);
      await reload();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Delete failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ops-fill-page ops-fill-page--bare">
      <div className="ops-fill-page__grid-host">
        <DataGrid
          ref={gridRef}
          dataSource={rows}
          keyExpr="id"
          height="100%"
          width="100%"
          showBorders
          columnAutoWidth
          columnHidingEnabled
          allowColumnResizing
          allowColumnReordering
          columnResizingMode="widget"
          {...OPS_SITE_GRID_PROPS}
          noDataText={error ?? (loading ? 'Loading…' : 'No pharmacies yet.')}
          onRowDblClick={(e) => onGridRowDblClick(e, openEdit)}
        >
          <OpsGridStateStoring
            storageKey={stateStorageKey}
            schemaVersion={OUR_PHARMACIES_GRID_SCHEMA_VERSION}
            columns={OUR_PHARMACIES_GRID_COLUMNS}
          />
          <Toolbar>
            <PatientTrackingGridToolbarItems
              gridRef={gridRef}
              loading={loading}
              onRefresh={() => void reload()}
              newItem={{ hint: 'Add pharmacy', onClick: openCreate }}
              export={{ fileName: 'our-pharmacies', worksheetName: 'Our Pharmacies', hint: 'Export pharmacies' }}
            />
            <Item name="searchPanel" location="after" />
          </Toolbar>
          <SearchPanel visible width={260} placeholder="Search pharmacies…" />
          <HeaderFilter visible />
          <ColumnChooser enabled mode="select" allowSearch title="Choose columns" />
          <Scrolling mode="standard" scrollByThumb showScrollbar="onScroll" useNative />
          <Paging defaultPageSize={50} />

          <Column dataField="pharmacyName" caption="Pharmacy" minWidth={180} sortOrder="asc" />
          <Column dataField="legalName" caption="Legal Name" minWidth={180} visible={false} />
          <Column dataField="npi" caption="NPI" width={120} />
          <Column dataField="nabp" caption="NABP" width={120} />
          <Column dataField="mainPhone" caption="Main Phone" width={140} />
          <Column dataField="fax" caption="Fax" width={140} visible={false} />
          <Column dataField="email" caption="Email" minWidth={180} visible={false} />
          <Column dataField="website" caption="Website" minWidth={180} visible={false} />
          <Column dataField="city" caption="City" minWidth={130} />
          <Column dataField="state" caption="State" width={80} />
          <Column dataField="postalCode" caption="Postal" width={100} />
          <Column dataField="primaryContactName" caption="Primary Contact" minWidth={160} />
          <Column dataField="primaryContactEmail" caption="Contact Email" minWidth={180} visible={false} />
          <Column dataField="isActive" caption="Active" dataType="boolean" width={90} />
          <Column dataField="modified" caption="Modified" dataType="datetime" width={160} visible={false} />
          <Column dataField="modifiedBy" caption="Modified By" minWidth={140} visible={false} />
        </DataGrid>

        <GridFormPopup
          visible={editorOpen}
          title={editingId ? 'Edit pharmacy' : 'Add pharmacy'}
          onClose={() => setEditorOpen(false)}
          width={820}
          sizeStorageKey={opsGridFormPopupSizeKey(stateStorageKey)}
          error={formError}
          footer={
            <div className="patient-tracking-popup-footer">
              <div>
                {editingId ? (
                  <Button text="Delete" type="danger" disabled={saving} onClick={() => void remove()} />
                ) : null}
              </div>
              <div className="patient-tracking-popup-footer__actions">
                <Button text="Cancel" disabled={saving} onClick={() => setEditorOpen(false)} />
                <Button text={saving ? 'Saving…' : 'Save'} type="default" disabled={saving} onClick={() => void save()} />
              </div>
            </div>
          }
        >
          <div className="patient-form-grid">
            <label>
              <span className="patient-form-label patient-form-label--required">Pharmacy name</span>
              <TextBox value={form.pharmacyName} onValueChanged={(e) => setForm((f) => ({ ...f, pharmacyName: e.value ?? '' }))} />
            </label>
            <label>
              <span className="patient-form-label">Legal name</span>
              <TextBox value={form.legalName ?? ''} onValueChanged={(e) => setForm((f) => ({ ...f, legalName: e.value ?? '' }))} />
            </label>
            <label>
              <span className="patient-form-label">NPI</span>
              <TextBox value={form.npi ?? ''} onValueChanged={(e) => setForm((f) => ({ ...f, npi: e.value ?? '' }))} />
            </label>
            <label>
              <span className="patient-form-label">NABP</span>
              <TextBox value={form.nabp ?? ''} onValueChanged={(e) => setForm((f) => ({ ...f, nabp: e.value ?? '' }))} />
            </label>
            <label>
              <span className="patient-form-label">Address line 1</span>
              <TextBox value={form.addressLine1 ?? ''} onValueChanged={(e) => setForm((f) => ({ ...f, addressLine1: e.value ?? '' }))} />
            </label>
            <label>
              <span className="patient-form-label">Address line 2</span>
              <TextBox value={form.addressLine2 ?? ''} onValueChanged={(e) => setForm((f) => ({ ...f, addressLine2: e.value ?? '' }))} />
            </label>
            <label>
              <span className="patient-form-label">City</span>
              <TextBox value={form.city ?? ''} onValueChanged={(e) => setForm((f) => ({ ...f, city: e.value ?? '' }))} />
            </label>
            <label>
              <span className="patient-form-label">State</span>
              <TextBox value={form.state ?? ''} onValueChanged={(e) => setForm((f) => ({ ...f, state: e.value ?? '' }))} />
            </label>
            <label>
              <span className="patient-form-label">Postal code</span>
              <TextBox value={form.postalCode ?? ''} onValueChanged={(e) => setForm((f) => ({ ...f, postalCode: e.value ?? '' }))} />
            </label>
            <label>
              <span className="patient-form-label">Country</span>
              <TextBox value={form.country ?? ''} onValueChanged={(e) => setForm((f) => ({ ...f, country: e.value ?? '' }))} />
            </label>
            <label>
              <span className="patient-form-label">Main phone</span>
              <TextBox value={form.mainPhone ?? ''} onValueChanged={(e) => setForm((f) => ({ ...f, mainPhone: e.value ?? '' }))} />
            </label>
            <label>
              <span className="patient-form-label">Fax</span>
              <TextBox value={form.fax ?? ''} onValueChanged={(e) => setForm((f) => ({ ...f, fax: e.value ?? '' }))} />
            </label>
            <label>
              <span className="patient-form-label">Email</span>
              <TextBox value={form.email ?? ''} onValueChanged={(e) => setForm((f) => ({ ...f, email: e.value ?? '' }))} />
            </label>
            <label>
              <span className="patient-form-label">Website</span>
              <TextBox value={form.website ?? ''} onValueChanged={(e) => setForm((f) => ({ ...f, website: e.value ?? '' }))} />
            </label>
            <label>
              <span className="patient-form-label">Primary contact name</span>
              <TextBox value={form.primaryContactName ?? ''} onValueChanged={(e) => setForm((f) => ({ ...f, primaryContactName: e.value ?? '' }))} />
            </label>
            <label>
              <span className="patient-form-label">Primary contact title</span>
              <TextBox value={form.primaryContactTitle ?? ''} onValueChanged={(e) => setForm((f) => ({ ...f, primaryContactTitle: e.value ?? '' }))} />
            </label>
            <label>
              <span className="patient-form-label">Primary contact email</span>
              <TextBox value={form.primaryContactEmail ?? ''} onValueChanged={(e) => setForm((f) => ({ ...f, primaryContactEmail: e.value ?? '' }))} />
            </label>
            <label>
              <span className="patient-form-label">Primary contact phone</span>
              <TextBox value={form.primaryContactPhone ?? ''} onValueChanged={(e) => setForm((f) => ({ ...f, primaryContactPhone: e.value ?? '' }))} />
            </label>
            <label className="patient-form-grid__full">
              <span className="patient-form-label">Notes</span>
              <TextArea
                value={form.notes ?? ''}
                autoResizeEnabled
                minHeight={70}
                onValueChanged={(e) => setForm((f) => ({ ...f, notes: e.value ?? '' }))}
              />
            </label>
            <label>
              <CheckBox
                text="Active pharmacy"
                value={form.isActive}
                onValueChanged={(e) => setForm((f) => ({ ...f, isActive: e.value !== false }))}
              />
            </label>
          </div>
        </GridFormPopup>
      </div>
    </div>
  );
}
